/**
 * French interface strings, Canadian usage.
 *
 * The two groups quoted verbatim from ux.md in `en-CA` carry the same obligations here:
 *
 * - `sync.*` — the plain-language sync states. A volunteer learns these five once and then reads
 *   them on every surface, so they are short sentences rather than technical labels. *"Couldn't
 *   send — will retry"* becomes a promise in French too, not a diagnosis.
 * - `registration.state.*` — a closed window says what to do next. *"see the desk at the gate"*
 *   is the actionable half of that sentence and survives translation intact.
 *
 * Typographic conventions follow Canadian French rather than European: no space before `:` or
 * `?`, and `…` rather than three periods. Apostrophes are typographic (`’`) because these strings
 * are rendered, not parsed.
 */

import type { Messages } from "../index.js";

export const frCA: Messages = {
  // Synchronisation — ux.md §2, « ne jamais bloquer sur le réseau ».
  "sync.savedLocally": "Enregistré sur cet appareil",
  "sync.sending": "Envoi en cours…",
  "sync.sent": "Envoyé",
  "sync.failed": "Envoi impossible — nouvelle tentative à venir",
  "sync.conflict": "À vérifier",
  "sync.pendingCount": "{count} en attente d’envoi",
  "sync.offline": "Aucun signal — vous pouvez continuer",

  // État des inscriptions — ux.md §3.1.
  "registration.state.opensAt": "Les inscriptions ouvrent {when}",
  "registration.state.closesIn": "Les inscriptions ferment {relative}",
  "registration.state.open": "Les inscriptions sont ouvertes",
  "registration.state.closed":
    "Les inscriptions sont fermées — présentez-vous au comptoir à l’entrée",
  "registration.state.notScheduled": "Les heures d’inscription seront annoncées",
  "registration.override.prompt": "Les inscriptions ont fermé à {when} — inscrire quand même?",
  "registration.override.reasonLabel": "Motif de l’inscription après la fermeture",

  // Arrivées — ux.md §3.4.
  "checkIn.already": "Déjà enregistré à {when}",
  "checkIn.confirmed": "Arrivée enregistrée",
  // "Annuler" alone would be ambiguous here: French uses one word for both *undo* and *cancel*,
  // and this button sits beside a dismiss action for several seconds (ux.md §3.4). Naming what is
  // being undone is what keeps a reversal from reading as "close this message".
  "checkIn.undo": "Annuler l’arrivée",
  "checkIn.counter": "{checkedIn} arrivés · {expected} attendus",

  // Jugement — ux.md §3.6.
  "judge.signInPrompt": "Entrez votre code d’accès",
  "judge.abstain": "S’abstenir",
  "judge.abstainReason": "Pourquoi vous abstenez-vous?",
  "judge.nextDancer": "Danseur suivant",
  "judge.runningTotal": "Total",
  "judge.blindMode": "Les noms sont masqués pour cette compétition",

  // Argent — ux.md §3.9.
  "payout.notYetPayable":
    "Pas encore payable — en attente des résultats approuvés pour cette catégorie",
  "payout.reasonRequired": "Motif",
  "payout.void": "Annuler ce versement",

  // Confirmations communes.
  "action.undo": "Annuler l’action",
  "action.cancel": "Abandonner",
  "action.save": "Enregistrer",
  "action.print": "Imprimer",
  "common.reasonRequired": "Un motif est obligatoire, et il est consigné avec votre nom.",
  "common.offlineNotice":
    "Vous êtes hors ligne. Tout ce que vous faites est enregistré et sera envoyé plus tard.",

  // Enveloppe d’erreur — les mots que rendent les codes d’erreur de l’API.
  "error.notFound": "Nous n’avons pas trouvé cet élément.",
  "error.forbidden": "Vous n’avez pas la permission de faire cela.",
  "error.conflict": "Quelqu’un d’autre a modifié ceci pendant que vous travailliez.",
  "error.validation": "Un élément de ce formulaire doit être corrigé.",
  "error.unavailable": "Cela ne fonctionne pas en ce moment. Votre travail est enregistré.",
  /** Une lecture qui n’a pas pu joindre l’API. Rien n’était en cours d’enregistrement, donc rien
   *  n’est promis. */
  "error.tryAgain": "Cela ne fonctionne pas en ce moment. Réessayez dans un instant.",

  /* ------------------------------------------------------------- le site public
   *
   * Tout ce qui suit est une chaîne écrite par le *logiciel*. Les mots de la communauté — noms
   * d’événements, de compétitions et de catégories, textes des annonces, prose d’`info_json`,
   * libellés du formulaire de l’organisateur — ne sont jamais des clés. Ils viennent de la base de
   * données dans la langue où la communauté les a écrits.
   *
   * Là où un mot du domaine apparaît dans une phrase française, la tournure évite volontairement
   * l’article accordé (« votre {category} », « chaque {category} », « parmi les {competitions} »).
   * Une communauté peut remplacer ce mot par un mot d’un autre genre, et « cette catégorie »
   * deviendrait alors faux. L’anglais n’a pas ce problème, ce qui explique que quelques modèles ne
   * placent pas le mot au même endroit — c’est précisément à cela que servent des modèles.
   */

  // La coquille, et la page de la plateforme.
  "site.skipToContent": "Aller au contenu",
  "site.language": "Langue",
  "site.community.description": "Événements de {community}",
  /* Les liens de section dans l’en-tête, page de la plateforme seulement. Assez courts pour tenir
     sur un téléphone, où la barre les fait passer à la ligne suivante. */
  "site.nav.label": "Sections du site",
  "site.nav.whatsOn": "À venir",
  "site.nav.communities": "Communautés",
  "site.nav.organisers": "Organisateurs",
  /* Le pied de page. `operatedBy` présente la marque du propriétaire ; la ligne de droits d’auteur
     à côté nomme déjà la même entreprise. */
  "site.footer.operatedBy": "Exploité par",
  "site.legal.privacy": "Confidentialité",
  "site.legal.terms": "Conditions d’utilisation",
  "site.legal.dataGovernance": "Gouvernance des données",
  "site.legal.updated": "Dernière mise à jour le {date}",
  /* La ligne sous la marque, sur la page de la plateforme, et sa « meta description ». Elle nomme
     ce que le visiteur cherche, non ce qu’est le logiciel — la description du logiciel, c’est
     « site.platform.blurb », plus bas sur cette même page. */
  "site.platform.tagline": "Trouvez un pow-wow — dates, programme, inscription et résultats",
  "site.platform.blurb":
    "Une gestion d’événements et de compétitions conçue pour le déroulement réel d’un pow-wow — des séances plutôt qu’une seule date limite, des juges choisis le jour même, et des règles de prix que chaque communauté rédige elle-même.",
  "site.platform.noCommunities": "Aucune communauté n’est encore inscrite.",
  "site.platform.lead":
    "Un pow-wow est un rassemblement — danse, tambour, nourriture et famille — qu’une communauté tient pour les siens et pour qui vient. C’est ici que les communautés présentes publient le leur : quand, où, comment s’y inscrire, et qui a gagné.",

  /* ------------------------------------------------ la page d’accueil de la plateforme (D72)
   *
   * Deux publics sur une même page, dans cet ordre : la personne qui cherche un pow-wow à
   * fréquenter, puis celle qui se demande ce qu’est un pow-wow, puis la communauté qui se demande
   * si elle tiendra le sien ici. Le ton ne vend rien : un visiteur cherche une date et un lieu, et
   * un organisateur juge le logiciel à la façon dont il décrit sa fin de semaine.
   */

  /* Les images du haut de la page. « note » y figure parce qu’il s’agit d’illustrations générées
     et non de photographies d’un rassemblement réel — une page qui annonce de vrais événements ne
     doit pas laisser croire le contraire. À remplacer quand de vraies photos autorisées arrivent. */
  "platform.hero.label": "Images d’un pow-wow",
  "platform.hero.note": "Illustrations — et non des photographies d’un rassemblement réel.",
  "platform.hero.alt.grandEntry":
    "Des danseurs entrent en procession dans l’arène pour la Grande Entrée, derrière un bâton d’aigle et des drapeaux.",
  "platform.hero.alt.arbour":
    "L’abri et le cercle de danse vus des gradins, des familles assises tout autour.",
  "platform.hero.alt.drum": "Un groupe de chanteurs assis autour d’un grand tambour.",
  "platform.hero.alt.dancers":
    "Trois danseuses sur l’herbe, vues de dos, en pleine danse, châles et franges en mouvement.",
  "platform.hero.alt.grounds":
    "Le terrain autour de l’arène par un après-midi d’été — kiosques, familles, enfants.",

  "platform.upcoming.heading": "Pow-wow à venir",
  "platform.upcoming.intro":
    "Tout ce qu’une communauté a publié sur cette plateforme, du plus proche au plus lointain. Chaque annonce mène à la page de la communauté, où se trouvent le programme, l’inscription et les résultats.",
  "platform.upcoming.none":
    "Rien n’est encore publié pour la saison qui vient. Dès qu’une communauté publie un événement, il paraît ici au moment même où il paraît sur sa propre page — personne n’a à l’annoncer deux fois.",
  "platform.upcoming.hostedBy": "Organisé par {community}",
  "platform.upcoming.browse": "Parcourir les communautés",

  "platform.directory.heading": "Trouver une communauté",
  "platform.directory.intro":
    "{count} communautés ont une page ici. Ouvrez-en une pour son programme, ses inscriptions et ses résultats. Une communauté qui n’a encore rien publié a tout de même sa page — elle attend son premier événement.",
  /* Le compte vaut un sur un déploiement neuf, et c’est la première phrase que l’on y lit. Une
     seconde chaîne plutôt qu’une règle de pluriel : le verbe s’accorde lui aussi. */
  "platform.directory.introOne":
    "Une communauté a une page ici. Ouvrez-la pour son programme, ses inscriptions et ses résultats. Une communauté qui n’a encore rien publié a tout de même sa page — elle attend son premier événement.",
  /* Neutre dans les deux états : un « summary » n’affiche qu’un seul libellé, ouvert ou fermé, et
     c’est le marqueur du navigateur qui dit lequel des deux. */
  "platform.directory.showAll": "Les {count} communautés",
  "platform.directory.showAllOne": "La seule communauté",
  "platform.directory.filter.label": "Rechercher par nom",
  "platform.directory.filter.placeholder": "Tapez une partie d’un nom",
  "platform.directory.filter.showing": "{shown} sur {total} affichées.",
  "platform.directory.filter.none": "Aucune communauté ne correspond à « {query} ».",
  "platform.directory.jump": "Aller à une lettre",
  "platform.directory.nextEvent": "Prochainement : {event}, {when}",

  "platform.about.heading": "Ce qui se passe à un pow-wow",
  "platform.about.what":
    "Un pow-wow est un rassemblement où les communautés des Premières Nations et des Amérindiens se réunissent pour danser, pour chanter et pour honorer leurs cultures. Certains durent un après-midi, d’autres une semaine. Certains sont des concours dotés de bourses ; d’autres sont des pow-wow traditionnels, tenus pour le rassemblement lui-même plutôt que pour un classement.",
  "platform.about.name":
    "Le mot vient du narragansett powwaw, « chef spirituel ». Les rassemblements publics sous une forme proche de celle d’aujourd’hui se sont répandus depuis les Grandes Plaines à la fin des années 1800 et au début des années 1900 — au fil de décennies où les gouvernements ont cherché à y mettre fin, et où les communautés ont continué de les tenir.",
  "platform.about.grandEntry":
    "Un pow-wow s’ouvre par la Grande Entrée : d’abord le bâton d’aigle, puis les drapeaux, puis les danseurs, qu’un tambour hôte fait entrer en chantant. Ce sont souvent des vétérans qui portent le bâton et les drapeaux. Une invocation, un chant des drapeaux et un chant des vétérans suivent avant que la danse commence.",
  "platform.about.dances":
    "La danse se déroule ensuite par catégorie — traditionnelle, herbe et fantaisie chez les hommes ; traditionnelle, robe à clochettes et châle de fantaisie chez les femmes — reprises le plus souvent chez les adolescents, les jeunes et les tout-petits, avec entre elles des danses intertribales ouvertes à toute personne présente.",
  "platform.about.staff":
    "L’équipe de tête tient la journée : un maître de cérémonie qui est la voix du rassemblement, un directeur d’arène, des danseurs de tête qui mènent chaque danse sur le plancher, et des tambours hôtes dont les chants portent la danse.",
  "platform.about.etiquetteHeading": "Si vous y allez",
  "platform.about.etiquette.regalia":
    "Ce que porte un danseur est une tenue d’apparat, jamais un costume. Une bonne part est faite à la main, et une part est héritée.",
  "platform.about.etiquette.photos":
    "Demandez avant de photographier, et attendez-vous à ce que certains moments soient entièrement interdits. Le maître de cérémonie dira lesquels.",
  "platform.about.etiquette.drum":
    "Le tambour est traité comme un être à part entière. Ne le touchez pas, et ne touchez la tenue de personne, sans y avoir été invité.",
  "platform.about.etiquette.mc":
    "Écoutez le maître de cérémonie. Se lever pour la Grande Entrée, pour les chants des drapeaux et pour les chants d’honneur est la consigne qui couvre l’essentiel de la journée.",
  "platform.about.source":
    "Un bref résumé, tiré de l’article de Wikipédia sur le pow-wow. Chaque communauté tient son rassemblement à sa manière, et c’est sa page ici qui fait autorité sur le sien.",
  "platform.about.sourceLink": "Pow-wow sur Wikipédia",

  "platform.software.heading": "Ce que fait le logiciel",
  "platform.software.intro":
    "Conçu pour le déroulement réel d’un pow-wow : dehors, une fois par année, par des bénévoles, un jour qui ne se reprend pas.",
  "platform.software.schedule.title": "Une fin de semaine faite de séances",
  "platform.software.schedule.body":
    "Grande Entrée, concours d’après-midi et de soir, intertribaux et versements restent distincts dans l’horaire, parce qu’un pow-wow n’est pas une seule date limite.",
  "platform.software.schedule.alt":
    "Horaire d’événement de démonstration montrant plusieurs séances pendant une fin de semaine de pow-wow.",
  "platform.software.registration.title": "L’inscription aux conditions de la communauté",
  "platform.software.registration.body":
    "Une période par séance plutôt qu’une seule date limite pour la fin de semaine, avec les catégories, les questions et les frais que la communauté a rédigés elle-même. Un danseur s’inscrit depuis son téléphone et conserve un lien vers sa propre inscription.",
  "platform.software.registration.alt":
    "Formulaire d’inscription sur téléphone avec une catégorie choisie automatiquement selon la date de naissance.",
  "platform.software.judging.title": "Un jury formé de ceux qui se sont présentés",
  "platform.software.judging.body":
    "Les juges reçoivent un code au tambour, et non un compte ouvert des semaines à l’avance. Un jury peut changer d’une séance à l’autre, les notes s’entrent sur le téléphone du juge, et les noms peuvent rester hors de la feuille là où la communauté le veut.",
  "platform.software.judging.alt":
    "Écran de notation d’un juge avec les catégories, les critères et l’état d’envoi.",
  /* La visionneuse. « enlarge » est une indication sur la vignette, masquée aux technologies
     d’assistance ; « close » nomme une vraie commande. */
  "platform.software.enlarge": "Voir en grand",
  "platform.software.close": "Fermer",
  "platform.software.payouts.title": "Les résultats, puis les bourses",
  "platform.software.payouts.body":
    "Des classements établis à partir des feuilles à mesure qu’elles arrivent, retenus jusqu’à ce que quelqu’un les approuve, publiés sur la page de la communauté — et reportés jusqu’à qui doit recevoir quoi.",
  "platform.software.payouts.alt":
    "La file des versements vue par la trésorerie : montants dus, montants versés et un bouton Payer à côté de chaque nom.",
  "platform.software.prizes.title": "Les règles de bourses écrites par la communauté",
  "platform.software.prizes.body":
    "Montants fixes, classements, sommes partagées et règles par séance peuvent coexister sans forcer la fin de semaine dans un modèle imposé.",
  "platform.software.prizes.alt":
    "Liste de règles de bourses montrant plusieurs structures de versement personnalisées pour un événement de démonstration.",
  "platform.software.offline.title": "Le concentrateur du site continue hors ligne",
  "platform.software.offline.body":
    "Quand Internet tombe, le concentrateur local garde l’accueil, l’inscription et la notation en marche, puis synchronise la file d’attente quand la connexion revient.",
  "platform.software.offline.alt":
    "Écran du concentrateur indiquant aucune connexion, une adresse locale, du travail en attente et l’état de synchronisation.",
  "platform.software.desk.title": "Un comptoir qui fonctionne à l’entrée",
  "platform.software.desk.body":
    "Inscrire quelqu’un à la table, valider les arrivées par code ou par nom, et voir qui est réellement là — sur le wifi du site, ou sans réseau du tout.",
  "platform.software.updates.title": "Des nouvelles qui atteignent les personnes déjà inscrites",
  "platform.software.updates.body":
    "Une annonce paraît sur la page de l’événement et part vers celles et ceux qui ont demandé à être avisés : un départ retardé, un changement d’arène, un enfant retrouvé.",
  "platform.software.paper.title": "Tout s’imprime",
  "platform.software.paper.body":
    "Rien d’important n’existe uniquement à l’écran. Programmes, feuilles de notation, listes de présence et coupons de confirmation s’impriment tous, parce qu’une pile à plat n’est pas une raison d’arrêter une compétition.",
  "platform.software.ownWords.title": "Les mots de la communauté",
  "platform.software.ownWords.body":
    "Les couleurs, le logo, les mots pour un danseur et une catégorie, les langues dans lesquelles la page répond — des réglages, non du code. Accueillir une communauté est un formulaire, non une mise en production.",

  /* L’entrée de l’organisateur depuis le haut de la page. Volontairement une phrase et un lien
     plutôt qu’un bouton : ils sont quelques lecteurs par année, et le visiteur n’en est pas. */
  "platform.organisers.prompt": "Vous tenez vous-même un pow-wow ?",
  "platform.organisers.promptLink": "Voir ce que fait le logiciel",

  "platform.organisers.heading": "Tenir votre pow-wow ici",
  "platform.organisers.body":
    "Chaque communauté de ce répertoire a sa page, son adresse et ses comptes de personnel, et rien de l’une n’est visible depuis l’autre. Si votre communauté tient un pow-wow et souhaite le tenir ici, écrivez-nous.",
  "platform.organisers.action": "Nous joindre",

  "site.sampleData.long":
    "Ces données servent d’exemple pour le développement. Il ne s’agit pas d’une véritable annonce d’événement.",
  "site.sampleData.short": "Données d’exemple — événement fictif.",

  // Revenir quelque part.
  "nav.backToEvent": "Retour à la page de l’événement",
  "nav.backToCommunity": "Retour à {community}",
  "nav.eventPage": "La page de l’événement",

  // La page d’accueil d’une communauté.
  "notFound.title": "Page introuvable",
  "notFound.heading": "Nous ne trouvons pas cette page",
  "notFound.body":
    "L\u2019adresse a peut-\u00eatre \u00e9t\u00e9 mal saisie, ou la page a \u00e9t\u00e9 retir\u00e9e. Tout ce que cette communaut\u00e9 a publi\u00e9 est li\u00e9 depuis sa page d\u2019accueil.",
  "notFound.noCommunity":
    "Cette adresse n\u2019appartient \u00e0 aucune communaut\u00e9 de ce site. V\u00e9rifiez l\u2019adresse sur l\u2019affiche ou le lien que vous avez suivi.",

  "home.onNow": "En cours",
  "home.comingUp": "À venir",
  "home.eventDetails": "Détails",
  "home.hero.label": "Images de {community}",
  "home.hero.alt": "Une image de {community}",
  /* Les surtitres de section sur la page d’une communauté. Courts : ils accompagnent le titre, ils
     ne le remplacent pas. */
  "home.welcomeEyebrow": "Bienvenue",
  "home.updatesEyebrow": "Dernières nouvelles",
  "home.programmeEyebrow": "Le programme",
  "home.contactEyebrow": "Coordonnées",
  "home.thanksEyebrow": "Avec le soutien de",
  "home.recentUpdates": "Nouvelles récentes",
  "home.allUpdates": "Toutes les nouvelles de {event} →",
  "home.noEvents": "Aucun événement n’a encore été publié.",
  "home.nothingElse": "Rien d’autre n’est encore prévu.",
  "home.finished": "Terminé",
  "home.getInTouch": "Nous joindre",
  "home.contactPending": "Les coordonnées seront publiées ici.",
  "home.communityWebsite": "Site Web de {community}",

  // La page de l’événement — ux.md §3.1.
  "event.cancelled.heading": "Cet événement est annulé",
  "event.cancelled.body":
    "Ne vous déplacez pas. Communiquez avec les organisateurs ci-dessous si vous avez des questions.",
  "event.addToCalendar": "Ajouter au calendrier",
  "event.updates": "Nouvelles",
  "event.schedule": "Horaire",
  "event.schedule.pending": "L’horaire sera annoncé.",
  "event.session.grandEntryTime": "{grandEntry} à {time}",
  "event.competitions.pending": "Les détails seront annoncés.",
  "event.roster": "Qui la dirige",
  "event.where": "Où ça se passe",
  "event.openInMaps": "Ouvrir dans une carte",
  "event.timezoneNote":
    "Toutes les heures sont indiquées selon {zone}, l’heure du lieu de l’événement.",

  // Les images (D71).
  "event.poster.alt": "Affiche de {event}",
  "event.poster.fullSize": "Voir l’affiche en grand format",
  "event.poster.group": "Affiches de {event}",
  "event.updates.images": "Photos accompagnant la nouvelle : {title}",
  "event.updates.imagesHint": "Faites glisser vers le côté pour en voir davantage.",

  // L’affichage plein écran, partagé par les affiches et les photos des nouvelles.
  "image.enlarge": "Toucher pour agrandir",
  "image.close": "Fermer",
  "image.original": "Ouvrir l’original",

  // Titres au-dessus de la prose d’`info_json`. Le titre est le nôtre, le texte est le leur.
  "event.info.generalRules": "Règlements",
  "event.info.registrationInstructions": "Comment s’inscrire",
  "event.info.parking": "Stationnement",
  "event.info.travel": "S’y rendre",
  "event.info.accommodation": "Où loger",
  "event.info.accessibility": "Accessibilité",
  "event.info.emergency": "En cas d’urgence",
  "event.info.cancellation": "En cas d’annulation",

  "announcement.pinned": "Épinglé",
  "announcement.urgent": "Urgent",
  "announcement.important": "Important",

  "sponsors.heading": "Avec nos remerciements à",

  "contact.whoToAsk": "À qui s’adresser",
  "contact.askAtDesk": "Informez-vous au comptoir des inscriptions à l’entrée.",
  // « Questions : » prendrait un deux-points; la tournure interrogative respecte la convention de
  // ce fichier (pas d’espace avant le point d’interrogation) sans perdre le sens.
  "contact.questions": "Des questions? {contact}",
  "contact.pleaseContact": "Communiquez avec {contact} pour en savoir plus.",

  // S’inscrire — ux.md §3.2.
  "registration.pageTitle": "Inscription — {event}",
  "registration.pageDescription": "S’inscrire à {event}",
  "registration.action.register": "S’inscrire",
  "registration.action.now": "S’inscrire maintenant",
  "registration.robotCheck": "Une vérification rapide : vous n’êtes pas un robot.",
  "registration.submit": "Envoyer mon inscription",
  "registration.aboutYou": "À propos de vous",
  "registration.permissions": "Autorisations",
  "registration.terms": "Conditions",
  "registration.noAccountNote":
    "Vous n’avez pas besoin de compte. Après l’envoi, vous recevrez votre {dancerNumber} et un lien qui vous permettra de modifier ou d’annuler votre inscription.",
  "registration.qrTitle": "Votre code d’inscription",
  "registration.status.label": "Statut",
  // Accordés au féminin : c’est l’inscription qui est active ou annulée.
  "registration.status.active": "Active",
  "registration.status.cancelled": "Annulée",
  "registration.registeredAt": "Date d’inscription",

  // Le bloc de base — affiché seulement là où le formulaire de la communauté ne demande rien.
  "registration.core.firstName": "Prénom",
  "registration.core.lastName": "Nom de famille",
  "registration.core.communityAffiliation": "Communauté d’origine",
  "registration.core.communityAffiliation.help":
    "Commencez à écrire et choisissez dans la liste, ou inscrivez-la vous-même — d’où que vous veniez.",
  "registration.core.age": "Âge",
  "registration.core.age.help":
    "Votre âge au moment du pow-wow. Sert à déterminer votre {category} d’âge. Vous pouvez modifier ce choix.",
  "registration.core.phone": "Numéro de téléphone",
  "registration.core.phone.help":
    "Pour que nous puissions vous joindre au sujet de votre inscription.",
  "registration.core.email": "Courriel",

  // La suite des quatre états honnêtes commencés dans `registration.state.*` plus haut.
  "registration.state.opensLater": "Les inscriptions ouvrent plus tard",
  "registration.state.opensOn": "Les inscriptions ouvrent {day} à {time}",
  "registration.state.closesAtTime": "Les inscriptions ferment à {time}",
  "registration.state.closesOn": "Les inscriptions ferment {day} à {time}.",
  "registration.state.closesInMinute": "Les inscriptions ferment dans {minutes} minute",
  "registration.state.closesInMinutes": "Les inscriptions ferment dans {minutes} minutes",
  "registration.state.openUntil": "Les inscriptions sont ouvertes jusqu’à {time}",
  "registration.state.notOpen": "Les inscriptions ne sont pas ouvertes.",
  "registration.state.notOpenForEvent": "Les inscriptions ne sont pas ouvertes pour cet événement.",
  // Les mêmes états, assez courts pour tenir à côté du nom d’une compétition dans une liste.
  "registration.state.short.opensLater": "Ouvre plus tard",
  "registration.state.short.notScheduled": "Heures à annoncer",
  "registration.state.short.closed": "Fermé",

  "registration.closed.heading": "Les inscriptions sont fermées",
  "registration.closed.body":
    "Les inscriptions sont fermées — présentez-vous au comptoir à l’entrée. Le personnel peut encore vous inscrire.",
  "registration.closed.deskCanRegister":
    "Le personnel du comptoir des inscriptions peut encore vous inscrire sur place. Présentez-vous à l’entrée et demandez.",
  "registration.notYetOpen.comeBack":
    "Revenez à l’ouverture des inscriptions, ou informez-vous au comptoir le jour même.",
  "registration.notOnline.heading": "Les inscriptions ne se font pas en ligne pour cet événement",
  "registration.notOnline.body":
    "Inscrivez-vous en personne au comptoir à l’entrée. Le personnel peut saisir vos renseignements pour vous.",

  // L’indicateur de progression. Écrit par le script d’amélioration, à qui ces mots sont transmis.
  "registration.progress.empty": "Rien de rempli pour l’instant",
  "registration.progress.complete": "Tout ce qu’il faut est rempli",
  // Le nombre rempli passe avant le total en français, à l’inverse de l’anglais.
  "registration.progress.count": "{done} éléments remplis sur {total}",

  // Choisir à quoi s’inscrire — ux.md §3.3.
  "registration.entries.legend": "À quoi vous inscrivez-vous?",
  "registration.entries.help":
    "Choisissez tout ce à quoi vous participez. Nous déterminons vos {categories} d’âge à partir de l’âge que vous avez indiqué lorsque c’est possible — vous pouvez modifier chacun de ces choix.",
  "registration.entries.chooseOne": "Vous devez faire au moins un choix parmi les {competitions}.",
  "registration.entries.enterCompetition": "S’inscrire à {name}",
  "registration.entries.count": "{entered} inscriptions sur {limit}",
  "registration.entries.full": "Complet",
  "registration.entries.closed": "Fermé",
  // Filtrer une longue liste de choix. Une compétition qui se tient en plusieurs séances a une case
  // par séance : douze catégories deviennent cinquante cases. Écrit par le script d’amélioration.
  "registration.entries.filter.label": "Rechercher dans la liste",
  "registration.entries.filter.placeholder": "p. ex. samedi après-midi",
  "registration.entries.filter.showing": "{shown} choix affichés sur {total}.",
  "registration.entries.filter.none":
    "Aucun résultat pour « {query} ». Videz la case pour tout revoir.",
  "registration.entries.filter.keptChosen":
    "{shown} choix affichés sur {total}, dont ceux que vous avez déjà faits.",
  "registration.entries.heading": "Inscriptions",
  "registration.entries.empty": "Aucune inscription pour l’instant.",
  "registration.entries.categoryPending": "{category} à confirmer au comptoir",

  "entry.status.entered": "Inscrit",
  "entry.status.withdrawn": "Retiré",
  "entry.status.noShow": "Marqué comme absent",
  "entry.status.disqualified": "Disqualifié",

  // Un champ et son étiquette. Les mentions et le choix vide sont au logiciel, pas à l’organisateur.
  "field.required": "(obligatoire)",
  "field.optional": "(facultatif)",
  "field.choose": "Choisir…",
  "field.error.required": "Ce renseignement est obligatoire.",
  "field.error.consentRequired": "Vous devez accepter ceci avant de vous inscrire.",
  "field.error.email": "Cela ne ressemble pas à une adresse courriel.",
  "field.error.phone": "Cela ne ressemble pas à un numéro de téléphone.",
  "field.error.date": "Utilisez une date comme 1990-06-21.",
  "field.error.number": "Ceci doit être un nombre.",

  // Les réponses proposées sur un champ libre — la liste des communautés (D46). Chacune de ces
  // phrases dit, d’une façon ou d’une autre, que la liste est un raccourci et non une contrainte.
  "field.suggest.open": "Afficher la liste",
  "field.suggest.hint": "Choisissez dans la liste ou écrivez votre propre réponse.",
  "field.suggest.showing": "Affichage de {shown} sur {total}.",
  "field.suggest.none":
    "Rien dans la liste ne correspond à « {query} ». Ce que vous écrivez est conservé.",
  "field.suggest.useTyped": "Utiliser « {value} » — absent de la liste",

  // Le coupon de confirmation — un bout de papier qui se trouve à être une page Web (D21).
  "confirmation.pageTitle": "Votre inscription est faite — {event}",
  "confirmation.heading": "Votre inscription est faite",
  "confirmation.badge": "Inscription faite.",
  "confirmation.printNote":
    "Imprimez cette page ou conservez le lien ci-dessous — vous aurez besoin du code à l’entrée.",
  "confirmation.missing.heading": "Nous ne pouvons pas afficher votre confirmation",
  "confirmation.missing.body":
    "Cette page a besoin du lien qui a été créé au moment de votre inscription.",
  "confirmation.missing.help":
    "Si vous êtes déjà inscrit, utilisez le lien de votre confirmation. En cas de doute, le comptoir à l’entrée peut vous retrouver.",
  "confirmation.beforeYouCome": "Avant de venir",

  // Inscription d’une équipe — un capitaine inscrit toute l’équipe (D57).
  "team.options.heading": "Vous inscrivez une équipe?",
  "team.options.help":
    "Certaines compétitions se font en équipe. Choisissez-en une pour inscrire toute l’équipe dans un seul formulaire.",
  "team.options.rosterNote": "{min} à {max} membres",
  "team.options.register": "Inscrire une équipe",
  "team.backToIndividual": "← M’inscrire plutôt à titre individuel",
  "team.about.legend": "Votre équipe — {competition}",
  "team.about.help": "Parlez-nous de l’équipe, puis ajoutez chaque membre ci-dessous.",
  "team.field.name": "Nom de l’équipe",
  "team.field.contactPhone": "Téléphone du contact",
  "team.field.contactEmail": "Courriel du contact",
  "team.field.session": "Séance",
  "team.member.legend": "Membre {n}",
  "team.member.captain": "Ce membre est le capitaine",
  "team.member.add": "Ajouter un membre",
  "team.member.remove": "Retirer ce membre",
  "team.roster.status": "{n} membres — cette équipe en demande de {min} à {max}",
  "team.submit": "Envoyer notre inscription",
  "team.noAccountNote":
    "Aucun compte n’est nécessaire. Après l’envoi, l’équipe reçoit son numéro et chaque membre est inscrit.",
  "team.error.notOpen":
    "L’inscription pour cette compétition en équipe n’est pas ouverte. Le personnel au comptoir peut encore inscrire l’équipe.",
  "team.error.tooFew": "Cette équipe a besoin d’au moins {min} membres.",
  "team.error.tooMany": "Cette équipe peut compter au plus {max} membres.",
  "team.confirmation.pageTitle": "Votre équipe est inscrite — {event}",
  "team.confirmation.heading": "Votre équipe est inscrite",
  "team.confirmation.badge": "Inscrite.",
  "team.confirmation.numberLabel": "Numéro d’équipe",
  "team.confirmation.deskNote":
    "Présentez ceci au comptoir d’inscription à l’arrivée de l’équipe. Chaque membre est inscrit.",
  "team.confirmation.missing.heading": "Impossible d’afficher la confirmation de l’équipe",
  "team.confirmation.missing.body":
    "Cette page a besoin du lien créé au moment de l’inscription de l’équipe.",

  // `/manage/{token}` — l’inscription d’un participant, sans compte.
  "manage.pageTitle": "Votre inscription — {name}",
  "manage.heading": "Votre inscription",
  "manage.action": "Gérer mon inscription",
  "manage.yourLink": "Votre lien:",
  "manage.linkNote":
    "Conservez-le. C’est ainsi que vous pourrez modifier ou annuler votre inscription, et c’est ce que contient le code ci-dessus.",
  "manage.printNote":
    "Utilisez la fonction d’impression de votre navigateur pour en garder une copie papier. Le code ci-dessus ouvre cette page.",
  "manage.checkInNote":
    "Ce n’est pas le code balayé à l’entrée. Celui-là se trouve dans votre courriel de confirmation — ouvrez « Afficher mon code d’arrivée ». Si vous ne le trouvez pas, le personnel du comptoir peut vous retrouver par votre nom.",
  "manage.notFound.heading": "Nous ne trouvons pas cette inscription",
  "manage.notFound.body":
    "Le lien a peut-être été mal saisi, ou il appartient à la page d’une autre communauté. Vérifiez le lien de votre confirmation, ou informez-vous au comptoir des inscriptions à l’entrée — on peut vous retrouver par votre nom.",
  "manage.entries.heading": "Vos inscriptions",

  // « Retirer » seul se confondrait avec « enlever de la page »; nommer ce qui est retiré garde au
  // geste son sens, comme pour `checkIn.undo`.
  "manage.withdraw.action": "Retirer mon inscription",
  "manage.withdraw.confirm": "Retirer votre inscription à {what}?",
  "manage.withdraw.warning":
    "Ce retrait ne peut pas être annulé ici. Le personnel du comptoir peut vous réinscrire si vous changez d’avis.",
  "manage.withdraw.reason": "Pourquoi vous retirez-vous?",
  "manage.withdraw.reasonHelp": "Facultatif. Cela aide les organisateurs à planifier.",
  "manage.withdraw.confirmAction": "Oui, retirez-moi",
  "manage.withdraw.keep": "Garder mon inscription",
  "manage.withdraw.done": "Cette inscription est retirée.",
  "manage.withdraw.disabled":
    "Le retrait en ligne n’est pas offert pour cet événement. Le comptoir des inscriptions peut le faire pour vous.",

  "manage.addEntry.heading": "S’inscrire à autre chose",
  "manage.addEntry.label": "Choisissez parmi les {competitions}",
  "manage.addEntry.action": "Ajouter",
  "manage.addEntry.choose": "Faites d’abord un choix parmi les {competitions}.",
  "manage.addEntry.done": "C’est ajouté à votre inscription.",

  "manage.contact.heading": "Vos coordonnées",
  "manage.contact.saved": "Vos coordonnées sont enregistrées.",
  "manage.contact.save": "Enregistrer mes coordonnées",
  "manage.contact.address": "Adresse",
  "manage.contact.city": "Municipalité",
  "manage.contact.region": "Province ou territoire",
  "manage.contact.postalCode": "Code postal",
  "manage.contact.country": "Pays",
  "manage.contact.emergencyName": "Personne à joindre en cas d’urgence",
  "manage.contact.emergencyPhone": "Téléphone de la personne à joindre en cas d’urgence",
  "manage.contact.disabled":
    "La modification en ligne n’est pas offerte pour cet événement. Le comptoir des inscriptions peut faire ces changements pour vous.",
  "manage.contact.registrarNote":
    "Votre nom, votre âge et votre {category} relèvent du registraire — informez-vous au comptoir si l’un de ces renseignements est erroné.",

  // `/c/{code}` — la page d’arrivée, et le code balayé à l’entrée (D66).
  "selfCheckIn.pageTitle": "Votre code d’arrivée — {name}",
  "selfCheckIn.heading": "Votre code d’arrivée",
  "selfCheckIn.showThis":
    "Montrez ceci à l’entrée. Le personnel le balaie pour indiquer que vous êtes arrivé.",
  "selfCheckIn.codeLabel": "Votre code, si la caméra n’arrive pas à lire le carré :",
  "selfCheckIn.link": "Votre page d’arrivée :",
  "selfCheckIn.printNote":
    "Utilisez la fonction d’impression de votre navigateur pour en garder une copie papier. Apportez-la, ou apportez votre téléphone.",
  "selfCheckIn.notFound.heading": "Nous ne trouvons pas ce code d’arrivée",
  "selfCheckIn.notFound.body":
    "Le code a peut-être été mal saisi, ou il appartient à la page d’une autre communauté. Demandez à l’entrée — le personnel peut vous trouver par votre nom ou votre numéro d’inscription.",
  "selfCheckIn.entries.heading": "Ce pour quoi vous vous présentez",
  "selfCheckIn.nothingToday": "Vous n’êtes inscrit à rien pour celle-ci.",
  "selfCheckIn.staffOnly":
    "L’arrivée pour cet événement est enregistrée par le personnel à l’entrée. Montrez le code ci-dessus en arrivant.",
  "selfCheckIn.chooseSession": "Pour laquelle êtes-vous ici ?",
  "selfCheckIn.chooseCompetition": "Pour laquelle vous présentez-vous ?",
  "selfCheckIn.action": "Enregistrer mon arrivée",
  "selfCheckIn.done": "Votre arrivée est enregistrée pour {session}.",
  "selfCheckIn.already": "Votre arrivée était déjà enregistrée pour {session}.",
  "selfCheckIn.undoNote":
    "S’il s’agit d’une erreur, dites-le au comptoir — l’annulation leur revient, car c’est à partir de là que les honoraires sont calculés.",
  "selfCheckIn.failed": "Cela n’a pas fonctionné. Réessayez, ou présentez-vous à l’entrée.",
  "selfCheckIn.needYourCode":
    "Ce téléphone n’a pas encore ouvert votre code d’arrivée, alors nous ne pouvons pas savoir qui vous êtes. Ouvrez « Afficher mon code d’arrivée » dans votre courriel de confirmation une fois, et cette affiche enregistrera ensuite votre arrivée d’une seule touche. Si vous ne trouvez pas le courriel, le personnel à l’entrée peut le faire pour vous.",

  "manage.survey.heading": "Dites aux organisateurs comment ça s’est passé",
  "manage.survey.body": "Quelques questions, et cela les aide à planifier l’an prochain.",
  "manage.survey.action": "Répondre au sondage",

  "manage.documents.heading": "Documents",
  "manage.documents.none": "Rien en attente.",
  "manage.documents.pending": "Encore à vérifier",
  "manage.documents.notVerified": "Pas encore vérifié",
  "manage.documents.uploadNote":
    "Il n’est pas encore possible de téléverser des documents ici. Apportez ce qui manque au comptoir des inscriptions à l’entrée et le personnel le consignera.",

  // Le tableau des résultats publics — ux.md §3.8.
  "results.title": "Résultats",
  "results.pageTitle": "Résultats — {event}",
  "results.pageDescription": "Résultats publiés pour {event}",
  "results.published": "Les résultats sont publiés →",
  "results.unavailable.heading": "Les résultats ne sont pas accessibles en ce moment",
  "results.unavailable.body": "Réessayez dans un moment, ou informez-vous à la table du {mc}.",
  "results.empty.heading": "Rien n’est encore publié",
  "results.empty.body":
    "Les résultats paraissent ici une fois que le pointage de chaque {category} a été vérifié et approuvé. Cela se fait au fil de la journée plutôt que d’un seul coup, alors revenez voir plus tard.",
  // La ligne se trouve déjà dans le tableau de la catégorie concernée, donc « ici » suffit et évite
  // un article accordé sur un mot que la communauté peut avoir remplacé.
  "results.empty.category": "Rien n’est encore publié ici.",
  "results.byNumberNote":
    "Cette communauté publie les résultats par {dancerNumber} plutôt que par nom.",
  "results.column.place": "Rang",
  "results.column.name": "Nom",
  "results.column.score": "Pointage",
  "results.adjusted": "Ajusté",
  "results.tieBreak": "Bris d’égalité",
  "results.adjustedNote":
    "Un organisateur a modifié ce classement après le calcul, en consignant un motif.",
  "results.tieBreakNote":
    "Ce classement a été départagé par la règle de bris d’égalité qui s’applique ici.",

  // Une page publique de réponse à un sondage.
  "survey.title": "Sondage",
  "survey.pageTitle": "Sondage — {community}",
  "survey.closed.heading": "Ce sondage n’est pas ouvert",
  "survey.closed.empty": "Il n’y a rien à répondre ici.",
  "survey.closed.body": "Il est peut-être fermé, ou il n’est peut-être pas encore ouvert.",
  "survey.thanks": "Merci.",
  "survey.recorded": "Vos réponses sont enregistrées.",
  "survey.anonymous":
    "Ce sondage est anonyme. Vos réponses ne sont associées ni à votre nom ni à votre inscription.",
  "survey.attributed":
    "Vos réponses sont associées à votre inscription pour que les organisateurs puissent faire un suivi.",
  "survey.submit": "Envoyer mes réponses",

  // Courriels. Chaque message dit de qui il vient et à quoi il sert, dans la langue du lecteur —
  // un courriel est souvent la seule surface qu’une personne consulte deux fois.
  "email.footer": "Envoyé par {community} au moyen de PowWow.",
  "email.buttonFallback": "Si le bouton ne fonctionne pas, collez ceci dans votre navigateur :",

  "email.magicLink.subject": "Votre lien de connexion pour {community}",
  "email.magicLink.heading": "Connexion",
  "email.magicLink.body":
    "Utilisez le bouton ci-dessous pour vous connecter. Le lien ne sert qu’une fois et expire dans 15 minutes.",
  "email.magicLink.action": "Se connecter",
  "email.magicLink.unexpected":
    "Si vous n’avez pas demandé à vous connecter, ignorez ce message — personne d’autre que vous ne peut utiliser ce lien.",

  "email.tempPassword.subject": "Votre mot de passe temporaire pour {community}",
  "email.tempPassword.heading": "Votre mot de passe temporaire",
  "email.tempPassword.body":
    "Connectez-vous avec ce mot de passe. On vous demandera aussitôt d’en choisir un nouveau.",
  "email.tempPassword.action": "Se connecter",
  "email.tempPassword.unexpected":
    "Ce mot de passe expire sous peu et ne sert qu’une fois. Si vous ne l’attendiez pas, dites-le aux personnes qui organisent vos événements.",

  "email.passwordReset.subject": "Réinitialisez votre mot de passe pour {community}",
  "email.passwordReset.heading": "Réinitialisez votre mot de passe",
  "email.passwordReset.body":
    "Utilisez le bouton ci-dessous pour choisir un nouveau mot de passe. Le lien ne sert qu’une fois et expire dans 30 minutes.",
  "email.passwordReset.action": "Choisir un nouveau mot de passe",
  "email.passwordReset.unexpected":
    "Si vous n’avez rien demandé, ignorez ce message — votre mot de passe n’a pas changé.",

  "email.invitation.subject": "Vous êtes invité à vous joindre à {community}",
  "email.invitation.heading": "Joignez-vous à {community}",
  "email.invitation.body": "Vous êtes invité à aider à organiser les événements de {community}.",
  "email.invitation.action": "Accepter l’invitation",

  "email.registration.subject": "Votre inscription à {event} est confirmée",
  // "L’inscription de {name}" would need an elision before a vowel — *d’Ivy*, not *de Ivy* — and a
  // template cannot know the next letter. Phrased to sidestep it entirely.
  "email.registration.body":
    "Inscription confirmée pour {name}. Voici tout ce que contient le coupon de confirmation.",
  "email.registration.bodyNoName":
    "Votre inscription est confirmée. Voici tout ce que contient le coupon de confirmation.",
  "email.registration.team": "Inscription avec {team}.",
  "email.registration.checkIn.action": "Afficher mon code d’arrivée",
  "email.registration.checkIn.qrAlt":
    "Votre code d’arrivée sous forme de carré à balayer. S’il ne s’affiche pas, utilisez le bouton ci-dessous.",
  "email.registration.checkIn.note":
    "Ouvrez ceci à l’entrée et le personnel balaie le carré à l’écran. Votre code est {code} — lisez-le à voix haute si la caméra n’arrive pas à faire la mise au point.",
  "email.registration.keepThis":
    "Conservez ce courriel — le comptoir peut vous trouver par votre nom, et le lien ci-dessus sert à modifier votre inscription.",

  "email.checkIn.subject": "Vous êtes enregistré à {event}",
  "email.checkIn.heading": "Arrivée enregistrée",
  "email.checkIn.body": "{name} est enregistré pour {session} à {at}.",
  "email.checkIn.bodyNoName": "Vous êtes enregistré pour {session} à {at}.",

  "email.announcement.subject": "Un message de {community}",
};
